import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatPriceLocal as formatPrice, fixAssetUrl, canonicalBase } from "../lib/api";
import { useCart } from "../lib/cart";
import { Check, Zap, ShieldCheck, Infinity as InfIcon, ChevronLeft, Award } from "lucide-react";
import SEO from "../components/SEO";
import SmartImage from "../components/SmartImage";
import RichTextView from "../components/RichTextView";
import NotFound from "./NotFound";

export default function ProductDetail() {
  const { slug } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(undefined); // undefined = loading, null = 404
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("desc");

  useEffect(() => {
    setProduct(undefined);
    api.get(`/products/${slug}`).then((r) => setProduct(r.data || null)).catch(() => setProduct(null));
  }, [slug]);

  if (product === null) return <NotFound/>;
  if (product === undefined) return <div className="pt-10 text-center text-zinc-500">Načítání…</div>;

  const inStock = (product.stock_count ?? 0) > 0;
  const base = canonicalBase();
  const productImage = fixAssetUrl(product.box_mockup || product.images?.[0] || "");

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.seo_description || product.short_description || product.description,
    image: productImage ? [productImage] : undefined,
    brand: { "@type": "Brand", name: product.brand || "Správný Směr" },
    sku: product.sku || product.slug,
    mpn: product.id,
    category: product.category_name || product.category_slug,
    offers: {
      "@type": "Offer",
      url: `${base}/produkt/${product.slug}`,
      priceCurrency: "CZK",
      price: product.price,
      availability: inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@type": "Organization", name: "Správný Směr" },
      priceValidUntil: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "CZ",
        returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
        merchantReturnDays: 14,
        returnMethod: "https://schema.org/ReturnByMail",
        returnFees: "https://schema.org/FreeReturn",
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: { "@type": "MonetaryAmount", value: 0, currency: "CZK" },
        shippingDestination: { "@type": "DefinedRegion", addressCountry: "CZ" },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 0, unitCode: "MIN" },
          transitTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 5, unitCode: "MIN" },
        },
      },
    },
    aggregateRating: (product.rating_count && product.rating_value) ? {
      "@type": "AggregateRating",
      ratingValue: product.rating_value,
      reviewCount: product.rating_count,
      bestRating: 5,
      worstRating: 1,
    } : undefined,
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Domů", item: `${base}/` },
      { "@type": "ListItem", position: 2, name: "Produkty", item: `${base}/produkty` },
      { "@type": "ListItem", position: 3, name: product.name, item: `${base}/produkt/${product.slug}` },
    ],
  };

  return (
    <div className="pt-8 pb-20" data-testid="product-detail-page">
      <SEO
        title={product.seo_title || product.name}
        description={product.seo_description || product.short_description}
        keywords={product.seo_keywords || `${product.name}, ${product.brand}, licence, klíč`}
        image={productImage}
        type="product"
        jsonLd={[jsonLd, breadcrumbLd]}
      />      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/produkty" className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 mb-6"><ChevronLeft className="w-4 h-4"/>Zpět na katalog</Link>
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <SmartImage
              testId="product-detail-main-image"
              wrapperClassName="bg-gradient-to-br from-zinc-50 to-zinc-100 rounded-3xl p-12 flex items-center justify-center aspect-square border border-zinc-100 relative overflow-hidden"
              candidates={[product.box_mockup, ...(product.images || [])]}
              alt={product.name}
              className="max-h-full max-w-full object-contain drop-shadow-2xl"
              loading="eager"
              fallback={
                <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center" data-testid="product-detail-image-fallback">
                  <div className="w-24 h-24 rounded-3xl bg-white border border-zinc-200 flex items-center justify-center shadow-lg mb-4">
                    <span className="font-display font-bold text-4xl text-zinc-300">{(product.brand || product.name || "?").charAt(0).toUpperCase()}</span>
                  </div>
                  <div className="text-xs uppercase tracking-widest text-zinc-400 font-semibold mb-1">{product.brand}</div>
                  <div className="text-base font-semibold text-zinc-500">{product.name}</div>
                </div>
              }
            />
            <div className="flex gap-3 mt-4">
              {(product.images || []).slice(0, 4).map((img, i) => (
                <div key={i} className="w-20 h-20 bg-zinc-50 rounded-xl border border-zinc-200 flex items-center justify-center p-2">
                  <img src={fixAssetUrl(img)} alt="" className="max-h-full max-w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">{product.brand}</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-3">{product.name}</h1>
            <p className="text-zinc-600 leading-relaxed mb-6">{product.short_description}</p>

            <div className="flex flex-wrap gap-2 mb-6">
              {product.labels?.includes("instant_delivery") && <Pill icon={Zap} text="Okamžité doručení"/>}
              {product.labels?.includes("lifetime") && <Pill icon={InfIcon} text="Trvalá licence"/>}
              {product.labels?.includes("online_activation") && <Pill icon={ShieldCheck} text="Online aktivace"/>}
            </div>

            <div className="bg-white border border-zinc-200 rounded-2xl p-6 mb-6">
              <div className="flex items-end gap-3 mb-2">
                {product.price_original && <div className="text-zinc-400 line-through text-lg">{formatPrice(product.price_original)}</div>}
                <div className="font-display font-bold text-4xl">{formatPrice(product.price)}</div>
              </div>
              <div className="text-xs text-zinc-500 mb-5">Konečná cena • Faktura automaticky po platbě</div>
              <div className="flex items-center gap-3 mb-5">
                <div className={`flex items-center gap-1.5 text-sm font-semibold ${inStock ? "text-emerald-600" : "text-red-600"}`} data-testid="stock-status">
                  <span className={`w-2 h-2 rounded-full ${inStock ? "bg-emerald-500" : "bg-red-500"}`}/>
                  {inStock ? `Skladem (${product.stock_count})` : "Vyprodáno"}
                </div>
              </div>
              <div className="flex gap-3 items-center">
                <div className="flex items-center border border-zinc-200 rounded-full">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-10 hover:bg-zinc-50 rounded-l-full">−</button>
                  <div className="w-10 text-center font-semibold">{qty}</div>
                  <button onClick={() => setQty(qty + 1)} className="w-9 h-10 hover:bg-zinc-50 rounded-r-full">+</button>
                </div>
                <button onClick={() => add(product, qty)} disabled={!inStock} className="btn-primary flex-1 py-3 rounded-full font-semibold disabled:bg-zinc-300 disabled:shadow-none" data-testid="buy-now-btn">Přidat do košíku</button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-xl border border-zinc-200 p-3"><Zap className="w-5 h-5 text-blue-600 mx-auto mb-1"/><div className="text-xs font-medium">Klíč do 2 min</div></div>
              <div className="rounded-xl border border-zinc-200 p-3"><Award className="w-5 h-5 text-emerald-600 mx-auto mb-1"/><div className="text-xs font-medium">Originál</div></div>
              <div className="rounded-xl border border-zinc-200 p-3"><ShieldCheck className="w-5 h-5 text-purple-600 mx-auto mb-1"/><div className="text-xs font-medium">Faktura PDF</div></div>
            </div>
          </div>
        </div>

        {/* TABS */}
        <div className="mt-16">
          <div className="flex gap-1 border-b border-zinc-200 mb-6">
            {[
              {id: "desc", label: "Popis"},
              {id: "specs", label: "Specifikace"},
              {id: "features", label: "Funkce"},
            ].map((tb) => (
              <button key={tb.id} onClick={() => setTab(tb.id)} className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === tb.id ? "border-blue-600 text-blue-600" : "border-transparent text-zinc-500 hover:text-zinc-900"}`} data-testid={`tab-${tb.id}`}>{tb.label}</button>
            ))}
          </div>
          {tab === "desc" && <RichTextView html={product.description} className="max-w-3xl"/>}
          {tab === "specs" && (
            <div className="max-w-3xl">
              {Object.entries(product.tech_specs || {}).map(([k, v]) => (
                <div key={k} className="flex border-b border-zinc-100 py-3"><div className="w-1/3 text-sm text-zinc-500">{k}</div><div className="w-2/3 text-sm font-medium">{v}</div></div>
              ))}
            </div>
          )}
          {tab === "features" && (
            <div className="grid sm:grid-cols-2 gap-3 max-w-3xl">
              {(product.features || []).map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-emerald-600 shrink-0"/>{f}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Pill({ icon: Icon, text }) {
  return <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-100"><Icon className="w-3.5 h-3.5"/>{text}</span>;
}
